import { Link, NavLink, useLocation } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useEffect, useState } from "react";
import { Menu, X, Languages } from "lucide-react";

const Navbar = () => {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: "/", label: t("nav.home"), tid: "nav-home" },
    { to: "/about", label: t("nav.about"), tid: "nav-about" },
    { to: "/services", label: t("nav.services"), tid: "nav-services" },
    { to: "/work", label: t("nav.work"), tid: "nav-work" },
    { to: "/methodology", label: t("nav.methodology"), tid: "nav-methodology" },
    { to: "/awards", label: t("nav.awards"), tid: "nav-awards" },
    { to: "/blog", label: t("nav.blog"), tid: "nav-blog" },
    { to: "/contact", label: t("nav.contact"), tid: "nav-contact" },
  ];

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#0A0A0C]/80 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between gap-6 md:gap-10 h-20">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 group shrink-0 me-4 md:me-8">
          <img
            src="/brand/logo.png"
            alt="ARTLENSS"
            className="h-10 md:h-12 w-auto object-contain drop-shadow-[0_2px_10px_rgba(230,169,91,0.25)]"
          />
          <span className="hidden sm:inline text-[11px] font-semibold uppercase tracking-[0.3em] text-[#E6A95B] border-s border-[#E6A95B]/30 ps-3">
            {lang === "ar" ? "استوديو · جدة" : "Studio · Jeddah"}
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={l.tid}
              className={({ isActive }) =>
                `text-sm uppercase tracking-[0.2em] font-medium transition-colors ${
                  isActive ? "text-[#E6A95B]" : "text-white/70 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-full border border-white/15 text-white/80 hover:text-[#E6A95B] hover:border-[#E6A95B]/50 transition"
            aria-label="Toggle language"
          >
            <Languages className="w-4 h-4" />
            <span className="text-xs font-semibold tracking-wider">{lang === "en" ? "AR" : "EN"}</span>
          </button>

          <Link
            to="/contact"
            data-testid="nav-cta-start"
            className="hidden md:inline-flex btn-primary text-sm py-2.5 px-5"
          >
            {t("nav.cta")}
          </Link>

          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#0A0A0C]/95 backdrop-blur-xl border-t border-white/10" data-testid="mobile-menu">
          <div className="container-x py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`${l.tid}-mobile`}
                className={({ isActive }) =>
                  `text-lg font-medium py-2 border-b border-white/5 ${
                    isActive ? "text-[#E6A95B]" : "text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="flex gap-3 mt-2">
              <button
                onClick={toggle}
                data-testid="lang-toggle-mobile"
                className="flex-1 py-3 rounded-full border border-white/15 text-white/90"
              >
                {lang === "en" ? "العربية" : "English"}
              </button>
              <Link to="/contact" className="flex-1 text-center btn-primary">
                {t("nav.cta")}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
